import * as React from 'react';
import { JSSClassesProps } from 'react-jss';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Lazy, Navigation, Pagination } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/lazy/lazy.min.css';
import { LazyOptions } from 'swiper/types';
import { Facility, Room } from '../../../../models/hotel-room-types';
import { ComponentJSSClasses } from './styles';
import { useMemo, useRef, useState } from 'react';
import { SliderItem } from '../SliderItem';
import { IconSliderNavigation } from '../../Icons/IconSliderNavigation';
import Button from 'sletat-ui-components2/dist/components/Button';
import { getVerbalPeople, getVerbalPlacements } from '../../../../utils/format';
import { RoomCardDetailed } from '../RoomCardDetailed';
import { checkIsRandomRoom } from '../../../../../utils/room';
import { useScrollDisable } from '../../../../../hooks/useScrollDisable';

SwiperCore.use([Pagination, Navigation, Lazy]);

export interface RoomCardProps {
    room: Room;
}

interface StyledRoomCardProps
    extends RoomCardProps,
        JSSClassesProps<ComponentJSSClasses> {}

const DEFAULT_PHOTO_PREVIEW_COUNT = 5;

export const RoomCard = ({ room, classes }: StyledRoomCardProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const isRandomRoom = useMemo(
        () => room.facilities?.find((f) => checkIsRandomRoom(f.id)),
        [room]
    );
    const navigationPrevRef = useRef<HTMLButtonElement>(null);
    const navigationNextRef = useRef<HTMLButtonElement>(null);
    let photos = room.photos ?? [];

    useScrollDisable(isOpen);

    function renderFacilities(f: Facility): JSX.Element | null {
        if (isRandomRoom) {
            return null;
        }
        return <span className={classes.facilityButton}>{f.name}</span>;
    }

    function renderRoomSize(room: Room) {
        let roomText = '';
        if (room.isRangeArea) {
            if (!room.minArea || !room.maxArea) return;

            roomText = `${room.minArea}-${room.maxArea}`;
        } else {
            if ((room.area ?? 0) <= 0) return;

            roomText = (room.area ?? 0).toString();
        }
        return (
            <>
                {roomText} м<sup>2</sup>
            </>
        );
    }

    function renderRoomCount(roomCount: number) {
        if (roomCount <= 0) return;

        return (
            <span>
                &nbsp;&middot;&nbsp;{roomCount} {getVerbalPlacements(roomCount)}
            </span>
        );
    }

    function renderRoomAccommodation(maxPerson: number) {
        if (maxPerson <= 0) return;

        return (
            <span>
                &nbsp;&middot;&nbsp; Максимум {maxPerson}{' '}
                {getVerbalPeople(maxPerson)}
            </span>
        );
    }

    function renderBullet(_index: any, className: any) {
        return photos.length > 1
            ? `<button class='${className} ${classes.sliderBullets}' type="button"/>`
            : ``;
    }

    const onPopupClose = () => {
        setIsOpen(false);
    };

    const onPopupOpen = () => {
        setIsOpen(true);
    };

    function swiperInit(swiper: SwiperCore) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        swiper.params.navigation.prevEl = navigationPrevRef.current;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        swiper.params.navigation.nextEl = navigationNextRef.current;
        swiper.navigation.init();
        swiper.navigation.update();
    }

    if (
        photos.length === 0 &&
        !room.description &&
        room.facilities.length === 0
    ) {
        return (
            <article className={classes.roomCard}>
                <div className={classes.roomPlaceholderImage} />
                <h3 className={classes.roomName}>{room.name}</h3>
                <div className={classes.roomPlaceholder}>
                    <span>
                        Туроператор не предоставил подробную информацию о номере
                    </span>
                </div>
            </article>
        );
    }

    return (
        <article className={classes.roomCard} onClick={onPopupOpen} role="button">
            {photos.length === 0 ? (
                <div className={classes.roomPlaceholderImage} />
            ) : (
                <section className={classes.sliderWrapper}>
                    {isRandomRoom && (
                        <div className={classes.randomTag}>Случайный</div>
                    )}
                    <Swiper
                        slidesPerView={1}
                        spaceBetween={20}
                        draggable={true}
                        lazy={
                            {
                                enabled: true,
                                checkInView: true,
                                loadPrevNext: true,
                            } as LazyOptions
                        }
                        preloadImages={true}
                        loop={true}
                        pagination={{
                            clickable: true,
                            clickableClass: `${classes.swiperPagination}`,
                            bulletActiveClass: `${classes.activeSliderBullets}`,
                            renderBullet,
                        }}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        onInit={swiperInit}
                        className={classes.swiper}
                    >
                        {photos
                            .slice(0, DEFAULT_PHOTO_PREVIEW_COUNT)
                            .map((photo, idx) => (
                                <SwiperSlide key={`${idx}_roomSlide`}>
                                    <SliderItem
                                        url={photo.url}
                                        title={photo.name}
                                    />
                                </SwiperSlide>
                            ))}

                        {photos.length > 1 && (
                            <>
                                <button
                                    className={classes.sliderNavButtonPrev}
                                    ref={navigationPrevRef}
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <IconSliderNavigation />
                                </button>
                                <button
                                    className={classes.sliderNavButtonNext}
                                    ref={navigationNextRef}
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <IconSliderNavigation />
                                </button>
                            </>
                        )}
                    </Swiper>
                </section>
            )}
            <section className={classes.mainContent} onClick={onPopupClose}>
                <h4 className={classes.roomName} title={room.name}>
                    {room.name}
                </h4>
                <p className={classes.roomParams}>
                    {renderRoomSize(room)}
                    {renderRoomCount(room.roomCount ?? 0)}
                    {renderRoomAccommodation(room.maxPersonCount ?? 0)}
                </p>
                <p className={classes.roomDescription}>{room.description}</p>
            </section>
            {room.facilities.length > 0 && (
                <div className={classes.roomFacilities}>
                    <div className={classes.roomFacilitiesButtons}>
                        {room.facilities.map((f, idx) => (
                            <React.Fragment key={`facility-${idx}`}>
                                {renderFacilities(f)}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
            <div className={classes.buttons}>
                <Button
                    onClick={onPopupOpen}
                    variant="outline"
                    className={classes.infoButton}
                >
                    Подробнее о номере
                </Button>
                {isOpen && <RoomCardDetailed room={room} onClose={onPopupClose} />}
            </div>
        </article>
    );
};
